/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2022, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'prop-types'
], (React, PropTypes) => {

	/**
	 * A React component which scales down a child to a maximum width.
	 * The `getNode` prop must return a DOM node (the "content" node) which both:
	 *     1. Is a descendant of this <WidthScaler>, i.e. one of the children (ideally THE
	 *        immediate ONLY child of <WidthScaler>)
	 *     2. Accurately represents the visible size of all content under this <WidthScaler>
	 * The <WidthScaler> will apply the necessary CSS styles to its own DOM element to cause the
	 * content to be no wider than the specified `maxWidth` prop.
	 *
	 * If the `browserErrorScale` is specified, the container height is modified. This is to
	 * compensate for browser bugs which erroneously calculate the size of of parents/children.
	 */
	return class WidthScaler extends React.Component {
		static propTypes = {
			children: PropTypes.arrayOf(PropTypes.node).isRequired,
			getNode: PropTypes.func.isRequired,
			maxWidth: PropTypes.number.isRequired,
			browserErrorScale: PropTypes.number,
			scale: PropTypes.number
		}

		static defaultProps = {
			browserErrorScale: 1.0
		};

		constructor(props) {
			super(props);
			this.ref = React.createRef();
		}

		componentDidMount() {
			this._updateScale();
		}

		/*
		 * Reads the width of the content, and scales the container such that the content will fit
		 * in the specified width. If we need to update the scale, this function has been written
		 * so that it can be called multiple times and should work correctly each time. For the
		 * moment, it seems that it's only necessary in componentDidMount(), but this can be
		 * expanded in the future, or possibly exposed via an optional prop.
		 */
		_updateScale() {
			if(!this.props.maxWidth) {
				return;	// If no maxium width specified, this is a no-op
			}
			const contentElement = this.props.getNode();
			if(!contentElement) {
				return;	// If content callback not correclty specified, this is a no-op
			}

			// Reset container styling so getBoundingClientRect() returns non-transformed
			// measurement.
			const containerElement = this.ref.current;
			containerElement.style.transform = '';
			containerElement.style.height = 'auto';

			// Get full size of content
			const naturalSize = contentElement.getBoundingClientRect();

			const scale = this.props.scale || this.props.maxWidth / naturalSize.width;
			let effectiveScale;
			// Only apply scale if there is horizontal overflow.
			// I.e. if content is smaller than available width, leave it at normal scale.
			if (scale < 1) {
				effectiveScale = scale;

				// Since transforms aren't included in DOM size calculations, we need to manually
				// set the container height to prevent unused portion just being filled with
				// whitespace.
				const scaledHeight = naturalSize.height * scale;
				containerElement.style.height = `${scaledHeight}px`;

				// In order to actually shrink the content, the origin needs to be in the top-left.
				containerElement.style.transformOrigin = 'left top';

				// Apply scale transform
				containerElement.style.transform = `scale(${scale})`;
			} else {
				effectiveScale = 1;
			}

			// Ensure container is the correct height for its content. This applies even if no
			// scaling has occurred.
			containerElement.style.height = `${naturalSize.height * effectiveScale * this.props.browserErrorScale}px`;
		}

		render() {
			return (
				<div className='widthScaler' ref={this.ref}>
					{this.props.children}
				</div>
			);
		}
	};
});
